import React, { useEffect, useState } from 'react';
import { getUsers, createUser, deleteUser, updateUserRole } from '../services/api';
import { ShieldAlert, Edit3, UserCheck, PlusCircle, Trash, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const ManageUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'user' });

  const fetchUsers = async () => {
    setLoading(true);
    const data = await getUsers();
    setUsers(data);
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser?.role === 'admin') fetchUsers();
  }, [currentUser]);

  if (currentUser?.role !== 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const res = await createUser(formData);
    if (res.success) {
      setFormData({ name: '', email: '', password: '', role: 'user' });
      setIsCreating(false);
      fetchUsers();
    } else {
      alert(res.message);
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser.id) {
      alert("You cannot delete your own account.");
      return;
    }
    if(window.confirm('Delete this user account permanently?')) {
      await deleteUser(id);
      fetchUsers();
    }
  };

  const handleRoleChange = async (id, newRole) => {
    if (id === currentUser.id) {
      alert("You cannot change your own role.");
      return;
    }
    await updateUserRole(id, newRole);
    fetchUsers();
  };

  const RoleBadge = ({ role }) => {
    if (role === 'admin') return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800"><ShieldAlert className="w-3 h-3 mr-1"/> Admin</span>;
    if (role === 'editor') return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800"><Edit3 className="w-3 h-3 mr-1"/> Editor</span>;
    return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-stone-100 text-stone-800"><UserCheck className="w-3 h-3 mr-1"/> User</span>;
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8 pt-2">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 mb-2">User Management</h1>
          <p className="text-stone-500">Create accounts and assign varying permission roles.</p>
        </div>
        <button 
          onClick={() => setIsCreating(!isCreating)}
          className="inline-flex items-center px-4 py-2.5 bg-saffron-600 text-white font-medium rounded-xl hover:bg-saffron-700 transition"
        >
          {isCreating ? 'Cancel' : <><UserPlus className="w-5 h-5 mr-2" /> Add User</>}
        </button>
      </div>

      {isCreating && (
        <div className="bg-stone-50 rounded-2xl border border-stone-200 p-6 mb-8 border-l-4 border-l-saffron-500">
          <h2 className="text-lg font-bold text-stone-800 mb-4">Register New User</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Full Name</label>
              <input required value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Email Address</label>
              <input required type="email" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="user@nathnagri.com" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Password</label>
              <input required minLength={6} type="password" value={formData.password} onChange={e=>setFormData({...formData, password: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="••••••••" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Role Assignment</label>
              <select value={formData.role} onChange={e=>setFormData({...formData, role: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 bg-white">
                <option value="user">Standard User (Can comment, rate, wait for blog approval)</option>
                <option value="editor">Editor (Can access CMS, approve blogs)</option>
                <option value="admin">Administrator (Full Access)</option>
              </select>
            </div>
            <div className="md:col-span-2 flex justify-end space-x-3 mt-4">
              <button type="submit" className="px-6 py-2.5 text-white bg-saffron-600 hover:bg-saffron-700 rounded-xl font-medium transition">Create Account</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-16 bg-stone-100 rounded-2xl w-full"></div>)}
        </div>
      ) : (
        <div className="overflow-hidden border border-stone-200 rounded-2xl">
          <table className="min-w-full divide-y divide-stone-200">
            <thead className="bg-stone-50">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Email</th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Role</th>
                <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-stone-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-stone-200">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-stone-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-semibold text-stone-900">{u.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-stone-500 text-sm">
                    {u.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end items-center space-x-3">
                    <select 
                      value={u.role} 
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      disabled={u.id === currentUser.id}
                      className="text-xs border border-stone-200 rounded-lg p-1.5 bg-stone-50 text-stone-600 disabled:opacity-50"
                    >
                      <option value="user">User</option>
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button 
                      onClick={() => handleDelete(u.id)} 
                      disabled={u.id === currentUser.id}
                      className="text-red-400 hover:text-red-600 disabled:opacity-30 p-1"
                    >
                      <Trash className="w-4 h-4"/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
